import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '../../shared/components/ui/Modal';
import { useGetTimeSlotsQuery, TimeSlotItem, useEnrollStudentInClassMutation, EnrollClassRequest } from '../../store/api/courseApi';
import { COLORS } from '../../theme/Colors';
import AppButton from '../../shared/components/ui/AppButton';
import EnrollmentConfirmationModal from './EnrollmentConfirmationModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { toast } from 'react-toastify';
import { parseSafeLocalDate, formatDate } from '../../utils/dateUtils';

interface EnrollmentSchedulerModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: number;
  courseName: string;
  fromDateString: string | null;
  endDateString: string | null;
  tutorId: number;
  onEnrollmentSuccess?: () => void;
}

const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};


const EnrollmentSchedulerModal: React.FC<EnrollmentSchedulerModalProps> = ({
  isOpen,
  onClose,
  classId,
  courseName,
  fromDateString,
  endDateString,
  tutorId,
  onEnrollmentSuccess,
}) => {
  const [currentSelectedDate, setCurrentSelectedDate] = useState<Date | null>(null);
  const [currentTimeSlot, setCurrentTimeSlot] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const student = useSelector((state: RootState) => state.auth.user);
  const [enrollStudentInClass, { isLoading: isEnrolling }] = useEnrollStudentInClassMutation();

  const parsedMinDate = useMemo(() => parseSafeLocalDate(fromDateString), [fromDateString]);
  const parsedMaxDate = useMemo(() => parseSafeLocalDate(endDateString), [endDateString]);
  const formattedSelectedDateForAPI = currentSelectedDate ? formatDateToYYYYMMDD(currentSelectedDate) : '';

  const { data: timeSlotsData, isLoading: isLoadingTimeSlots, error: timeSlotsError } = useGetTimeSlotsQuery(
    { classId, selectedDate: formattedSelectedDateForAPI },
    { skip: !currentSelectedDate || !classId }
  );

  useEffect(() => {
    if (isOpen) {
      let initialDateToSet: Date | null = null;
      if (parsedMinDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (parsedMinDate >= today) initialDateToSet = parsedMinDate;
        else if (parsedMaxDate && today <= parsedMaxDate && today >= parsedMinDate) initialDateToSet = today;
      }
      setCurrentSelectedDate(initialDateToSet);
      setCurrentTimeSlot(null);
      setIsConfirmModalOpen(false);
    } else {
      setCurrentSelectedDate(null);
      setCurrentTimeSlot(null);
      setIsConfirmModalOpen(false);
    }
  }, [isOpen, parsedMinDate, parsedMaxDate]);

  const highlightedDateRange = useMemo(() => {
    if (parsedMinDate && parsedMaxDate) return [{ "enabled-date-range-highlight": [parsedMinDate, parsedMaxDate] }];
    return [];
  }, [parsedMinDate, parsedMaxDate]);

  const handleDateChange = (date: Date | null) => {
    setCurrentSelectedDate(date);
    setCurrentTimeSlot(null);
  };

  const handleTimeSlotClick = (slot: TimeSlotItem) => {
    if (slot.isEnabled && currentSelectedDate) {
      setCurrentTimeSlot(slot.slot);
      setIsConfirmModalOpen(true);
    }
  };

  const handleConfirmEnrollment = async () => {
    if (!currentSelectedDate || !currentTimeSlot || !student?.id) {
      toast.warn("Missing selection details or student information. Please try again.");
      setIsConfirmModalOpen(false);
      return;
    }

    const enrollmentRequestBody: EnrollClassRequest = {
      studentId: student.id,
      enrolledDate: formatDateToYYYYMMDD(currentSelectedDate),
      timeSlot: currentTimeSlot,
      tutorId: tutorId,
    };

    try {
      await enrollStudentInClass({ classId, body: enrollmentRequestBody }).unwrap();
      toast.success(`Successfully enrolled in ${courseName} on ${formatDate(currentSelectedDate)} at ${currentTimeSlot}!`);
      setIsConfirmModalOpen(false);
      onClose();
      if (onEnrollmentSuccess) {
        onEnrollmentSuccess();
      }
    } catch (err: any) {
      console.error("Enrollment API failed:", err);
      const apiErrorMessage = err.data?.message || err.message || "Enrollment failed. Please try again.";
      toast.error(`Error: ${Array.isArray(apiErrorMessage) ? apiErrorMessage.join(', ') : apiErrorMessage}`);
    }
  };

  const dpSelected = currentSelectedDate === null ? undefined : currentSelectedDate;
  const dpMinDate = parsedMinDate === null ? undefined : parsedMinDate;
  const dpMaxDate = parsedMaxDate === null ? undefined : parsedMaxDate;

  return (
    <>
      <Modal isOpen={isOpen && !isConfirmModalOpen} onClose={onClose}>
        <ModalContent>
          <ModalTitle>Select Date and Time</ModalTitle>
          <CalendarContainer>
            <DatePicker
              selected={dpSelected}
              onChange={handleDateChange}
              minDate={dpMinDate}
              maxDate={dpMaxDate}
              highlightDates={highlightedDateRange}
              inline
              dateFormat="MMMM d, yyyy"
            />
          </CalendarContainer>

          {currentSelectedDate && (
            <TimeSlotsSection>
              <SectionTitle>Available Time Slots for {formatDate(currentSelectedDate)}:</SectionTitle>
              {isLoadingTimeSlots && <LoadingText>Loading time slots...</LoadingText>}
              {timeSlotsError && <ErrorText>Error fetching time slots. Please try again.</ErrorText>}
              {timeSlotsData && timeSlotsData.timeSlotArray?.length > 0 && (
                <TimeSlotsGrid>
                  {timeSlotsData.timeSlotArray.map((slot, index) => (
                    <TimeSlotButton
                      key={index}
                      disabled={!slot.isEnabled}
                      isSelected={currentTimeSlot === slot.slot && isConfirmModalOpen}
                      onClick={() => handleTimeSlotClick(slot)}
                    >
                      {slot.slot}
                    </TimeSlotButton>
                  ))}
                </TimeSlotsGrid>
              )}
              {timeSlotsData?.timeSlotArray?.length === 0 && (
                <NoSlotsText>No available time slots for this date.</NoSlotsText>
              )}
            </TimeSlotsSection>
          )}
          <CloseButtonContainer>
            <AppButton title="Back" onClick={onClose} style={{ marginTop: '20px', backgroundColor: COLORS.textMuted }} />
          </CloseButtonContainer>
        </ModalContent>
      </Modal>

      <EnrollmentConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmEnrollment}
        courseName={courseName}
        selectedDate={currentSelectedDate}
        selectedTimeSlot={currentTimeSlot}
        isLoading={isEnrolling}
      />
    </>
  );
};

export default EnrollmentSchedulerModal;

const ModalContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${COLORS.white};
  color: ${COLORS.textDark};
  width: 100%;
  border-radius: 16px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ModalTitle = styled.h2`
  color: ${COLORS.primary};
`;

const CalendarContainer = styled.div`
  margin-bottom: 20px;
  border-radius: 8px;
  padding: 0;
  width: max-content;

  .react-datepicker {
    border: 1px solid ${COLORS.purpleLight};
    border-radius: 8px;
    font-family: inherit;
    background-color: ${COLORS.white};
    padding: 10px;
  }

  .react-datepicker__header {
    background-color: ${COLORS.white};
    border-bottom: none;
    padding-top: 8px;
  }

  .react-datepicker__current-month {
    color: #B85B21;
    font-size: 1.1rem;
    font-weight: 500;
    padding-bottom: 15px;
    text-align: left;
    margin-left: 10px;
  }

  .react-datepicker__navigation {
    top: 18px;
  }

  .react-datepicker__navigation-icon::before {
    border-color: #B85B21;
    border-width: 2px 2px 0 0;
    height: 7px;
    width: 7px;
  }

  .react-datepicker__navigation--previous {
    right: 40px;
    left: auto;
  }

  .react-datepicker__navigation--next {
    right: 15px;
  }

  .react-datepicker__day-names {
    margin-top: 5px;
    margin-bottom: -2px;
  }

  .react-datepicker__day-name {
    color: ${COLORS.accent};
    font-weight: 500;
    width: 2.2rem;
    line-height: 2.2rem;
    margin: 0.1rem;
  }

  .react-datepicker__day {
    color: ${COLORS.primary};
    font-weight: 400;
    width: 2.2rem;
    line-height: 2.2rem;
    margin: 0.1rem;
    border-radius: 0.3rem;
  }

  .react-datepicker__day--outside-month {
    color: #aaa;
    pointer-events: none;
  }

  .enabled-date-range-highlight {
    font-weight: 500;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range {
    background-color: ${COLORS.primary};
    color: ${COLORS.white};
    font-weight: bold;
  }

  .react-datepicker__day--selected.enabled-date-range-highlight {
    background-color: ${COLORS.primary};
    color: ${COLORS.white};
  }

  .react-datepicker__day--keyboard-selected {
    background-color: ${COLORS.accent};
    color: ${COLORS.white};
  }

  .react-datepicker__day:hover {
    background-color: ${COLORS.purpleLight};
    border-radius: 0.3rem;
  }

  .react-datepicker__day--disabled {
    color: #ccc !important;
    cursor: not-allowed;
    background-color: transparent !important;
  }

  .react-datepicker__day--disabled:hover {
    background-color: transparent !important;
  }
`;

const TimeSlotsSection = styled.div`
  width: 100%;
  margin-top: 10px;
  text-align: center;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  color: ${COLORS.primary};
  margin-bottom: 10px;
`;

const TimeSlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-top: 10px;
`;

const TimeSlotButton = styled.button<{ disabled?: boolean; isSelected?: boolean }>`
  padding: 10px;
  border: 1px solid ${props => props.isSelected ? COLORS.accent : COLORS.primary};
  background-color: ${props => props.isSelected ? COLORS.accent : (props.disabled ? COLORS.backgroundGrey : COLORS.white)};
  color: ${props => props.isSelected ? COLORS.white : (props.disabled ? COLORS.textMuted : COLORS.primary)};
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  font-weight: 500;
  &:hover:not(:disabled) {
    background-color: ${props => props.isSelected ? COLORS.accent : COLORS.purpleLight};
    border-color: ${props => props.isSelected ? COLORS.accent : COLORS.primary};
  }
`;

const LoadingText = styled.p`
  color: ${COLORS.textDark};
`;

const ErrorText = styled.p`
  color: ${COLORS.errorRed};
`;

const NoSlotsText = styled.p`
  color: ${COLORS.textMuted};
`;

const CloseButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;
